import { NoPhotography } from '@mui/icons-material'
import { ButtonBase, Card, CircularProgress, Grid, Theme, Typography } from '@mui/material'
import * as controllers from 'controllers'
import { AddDamagePhotoType } from 'dtos'
import { useEffect, useState } from 'react'

interface DamagePhotoCardProps {
  complete?: boolean
  disabled?: boolean
  fileName?: string
  label?: string
  onClick?: () => void
  type?: AddDamagePhotoType
  vehicleGuid?: string
  vehicleDamageGuid?: string
  warning?: boolean
  photoUrl?: string
  damagePhotoFileName?: string
  isRedCard?: boolean
  icon?: React.ReactNode
}

export default function DamagePhotoCard({
  complete,
  disabled,
  label,
  onClick,
  fileName,
  type,
  vehicleGuid,
  vehicleDamageGuid,
  warning,
  photoUrl,
  damagePhotoFileName
}: DamagePhotoCardProps) {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [photoFileBase64, setPhotoFileBase64] = useState<string>('')
  const [format, setFormat] = useState<string>('')

  const getColor = (theme: Theme): string => {
    if (disabled) {
      return theme.palette.grey[400]
    } else if (complete) {
      return theme.palette.success.main
    } else if (warning) {
      return theme.palette.warning.main
    }

    return theme.palette.error.main
  }

  const getLabel = () => {
    if (label) {
      return label
    }

    switch (type) {
      case 'damage-photo-1':
        return 'Damage Photo 1'
      case 'damage-photo-2':
        return 'Damage Photo 2'
      case 'damage-photo-3':
        return 'Damage Photo 3'
      default:
        return ''
    }
  }

  useEffect(() => {
    if (fileName && vehicleGuid && vehicleDamageGuid) {
      setFormat(fileName.split('.')[fileName.split('.').length - 1])
      setIsLoading(true)
      controllers
        .getDamagePhoto1PhotoBase64(vehicleGuid, fileName)
        .then(base64 => {
          setPhotoFileBase64(base64)
        })
        .finally(() => {
          setIsLoading(false)
        })
    } else {
      setPhotoFileBase64('')
    }
  }, [fileName, vehicleGuid, vehicleDamageGuid])

  const getImageSource = () => {
    if (damagePhotoFileName) {
      return `/photos/${damagePhotoFileName}`
    } else if (photoFileBase64) {
      return `data:image/${format};base64,${photoFileBase64}`
    } else if (photoUrl) {
      return photoUrl
    } else {
      return ''
    }
  }
  const isUploaded = !!(damagePhotoFileName || photoFileBase64 || photoUrl)

  const isDamagePhoto2Or3 = type === 'damage-photo-2' || type === 'damage-photo-3'

  return (
    <Card
      component={ButtonBase}
      onClick={onClick}
      sx={theme => ({
        backgroundColor: isUploaded
          ? theme.palette.success.main
          : isDamagePhoto2Or3
          ? theme.palette.error.main
          : getColor(theme),
        color: '#fff',
        p: 1,
        width: '220px',
        height: '144px',
        maxHeight: '144px',
        borderRadius: '8px'
      })}
    >
      <Grid container spacing={0} alignItems='center' justifyContent='center'>
        <Grid item sx={{ minHeight: 112.5, maxHeight: 112.5 }}>
          {isLoading && <CircularProgress sx={{ color: '#fff', mt: 5 }} />}
          {!isLoading && getImageSource() ? (
            <img alt={type} src={getImageSource()} width={200} height={112.5} style={{ objectFit: 'cover' }} />
          ) : (
            <NoPhotography fontSize='large' sx={{ height: 112.5 }} />
          )}
        </Grid>

        <Grid item xs={12}>
          <Typography align='center' variant='body2'>
            {getLabel()}
          </Typography>
        </Grid>
      </Grid>
    </Card>
  )
}
